/**
 * The one place that answers: does this session belong to someone with an
 * ACCOUNT, or to a visitor who is still just designing?
 *
 * WHY THIS IS NOT `!user.is_anonymous`. This project has email confirmation
 * enabled, and Supabase converts an anonymous user in two stages: at
 * `updateUser({email, password})` the password is set immediately but the
 * address is parked in `new_email` and `is_anonymous` STAYS TRUE until the
 * emailed link is clicked. Production data says that gap is real and long —
 * every email signup gets a confirmation mail, none auto-confirm, and roughly a
 * quarter are never confirmed at all.
 *
 * Reading the raw claim during that window tells us a paying customer is an
 * anonymous visitor, which is how they end up metered against an exhausted
 * free-turn counter and shown a create-an-account form they already filled in,
 * while holding the credits they just bought.
 *
 * A pending `new_email` is proof the upgrade happened, so it counts. The
 * account is not yet *verified* — that is a separate question, and confirming
 * it is still required for anything that emails them — but it is committed,
 * which is what every caller here is actually asking about.
 */

export interface AccountStatusUser {
  is_anonymous?: boolean;
  new_email?: string | null;
  email?: string | null;
  email_confirmed_at?: string | null;
}

const ANONYMOUS_EMAIL_SUFFIX = "@anon.invalid";

function usableEmail(value: string | null | undefined): string | null {
  const email = value?.trim();
  if (!email || email.toLowerCase().endsWith(ANONYMOUS_EMAIL_SUFFIX)) {
    return null;
  }
  return email;
}

/**
 * True only for a visitor with no account at all: an anonymous session that has
 * not been through /api/auth/upgrade.
 */
export function isAnonymousVisitor(
  user: AccountStatusUser | null | undefined
): boolean {
  if (!user) return false;
  if (user.is_anonymous !== true) return false;
  // Upgrade in flight: password set, address awaiting confirmation.
  return !user.new_email;
}

/**
 * True once the visitor has started or completed an account upgrade.
 *
 * This is useful for account UI and confirmation reminders. It is deliberately
 * not strong enough for commerce: a pending `new_email` session still cannot
 * be recovered after the browser is lost.
 */
export function hasAccount(
  user: AccountStatusUser | null | undefined
): boolean {
  return Boolean(user) && !isAnonymousVisitor(user);
}

/**
 * True only after Supabase has converted the identity to a permanent user.
 * Use this stricter boundary for payments, submitted quotes, and rewards. A
 * pending email-change session works in the current browser but is not yet a
 * recoverable account.
 */
export function hasVerifiedAccount(
  user: AccountStatusUser | null | undefined
): boolean {
  return Boolean(
    user &&
    user.is_anonymous !== true &&
    usableEmail(user.email) &&
    user.email_confirmed_at
  );
}

/**
 * Supabase keeps an upgraded anonymous identity in this intermediate state
 * until the customer follows the email-change confirmation link. The current
 * browser session is usable, but the account cannot be recovered elsewhere.
 */
export function isPendingAccountConfirmation(
  user: AccountStatusUser | null | undefined
): boolean {
  return user?.is_anonymous === true && usableEmail(user.new_email) !== null;
}

/**
 * True only when an account handoff must carry work off the current Auth row.
 *
 * This is intentionally broader than isAnonymousVisitor: a pending email
 * confirmation is already an account for UI purposes, but Supabase still
 * stores it on the anonymous source row. If the customer instead proves an
 * existing password/OAuth account, that source work must be adopted before the
 * session cookie is replaced. Do not use this for payments or navigation.
 */
export function needsAnonymousWorkHandoff(
  user: AccountStatusUser | null | undefined
): boolean {
  return isAnonymousVisitor(user) || isPendingAccountConfirmation(user);
}

/**
 * Returns the address that belongs to the account being created or used.
 *
 * While an anonymous-user upgrade awaits confirmation, Supabase leaves the
 * synthetic auth/profile address in place and exposes the address entered by
 * the customer as `new_email`. Anything customer-facing (Stripe, receipts,
 * confirmation reminders) must therefore prefer `new_email` and must never
 * leak the synthetic `@anon.invalid` address.
 */
export function getAccountEmail(
  user: AccountStatusUser | null | undefined,
  profileEmail?: string | null
): string {
  return (
    (user?.is_anonymous === true ? usableEmail(user.new_email) : null) ??
    usableEmail(user?.email) ??
    usableEmail(profileEmail) ??
    ""
  );
}
